import React, { useState, useRef } from 'react';
import JSZip from 'jszip';
import './WebPConverter.css';

const WebPConverter = () => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [converting, setConverting] = useState(false);
  const [creatingZip, setCreatingZip] = useState(false);
  const [progress, setProgress] = useState({});
  const [quality, setQuality] = useState(0.8); // Default quality (80%)
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle dropped files
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const imageFiles = droppedFiles.filter(file => 
      file.type === 'image/jpeg' || 
      file.type === 'image/jpg' || 
      file.type === 'image/png'
    );
    
    addFiles(imageFiles);
  };

  // Handle file input change
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  // Add files to state
  const addFiles = (newFiles) => {
    const filesWithId = newFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      status: 'pending', // pending, converting, completed, error
      webpBlob: null,
      webpSize: null
    }));
    
    setFiles(prev => [...prev, ...filesWithId]);
  };

  // Convert image to WebP
  const convertToWebP = (file, customQuality = quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image'));
            }
          },
          'image/webp',
          customQuality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file.file);
    });
  };

  // Convert all files
  const convertAllFiles = async () => {
    setConverting(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.status !== 'pending') continue;

      try {
        // Update status to converting
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'converting' } : f
        ));

        setProgress(prev => ({ ...prev, [file.id]: 0 }));

        // Simulate progress
        const progressInterval = setInterval(() => {
          setProgress(prev => ({
            ...prev,
            [file.id]: Math.min((prev[file.id] || 0) + 20, 90)
          }));
        }, 100);

        // Convert to WebP
        const webpBlob = await convertToWebP(file);
        
        clearInterval(progressInterval);
        setProgress(prev => ({ ...prev, [file.id]: 100 }));

        // Update file with WebP data
        setFiles(prev => prev.map(f =>
          f.id === file.id
            ? {
                ...f,
                status: 'completed',
                webpBlob,
                webpSize: webpBlob.size
              }
            : f
        ));

      } catch (error) {
        console.error('Conversion failed:', error);
        setFiles(prev => prev.map(f =>
          f.id === file.id ? { ...f, status: 'error' } : f
        ));
      }
    }

    setConverting(false);
  };

  // Download all converted files as ZIP
  const downloadAsZip = async () => {
    const completedFiles = files.filter(f => f.status === 'completed');
    if (completedFiles.length === 0) return;

    setCreatingZip(true);
    const zip = new JSZip();
    
    // Add each WebP file to the ZIP
    completedFiles.forEach(file => {
      const fileName = file.name.replace(/\.(jpg|jpeg|png)$/i, '.webp');
      zip.file(fileName, file.webpBlob);
    });

    try {
      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `webp-images-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating ZIP file:', error);
    } finally {
      setCreatingZip(false);
    }
  };

  // Download single file (keep this for individual downloads)
  const downloadFile = (file) => {
    if (!file.webpBlob) return;

    const url = URL.createObjectURL(file.webpBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // Clear all files
  const clearAll = () => {
    setFiles([]);
    setProgress({});
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const completedFiles = files.filter(f => f.status === 'completed');
  const pendingFiles = files.filter(f => f.status === 'pending');
  const totalWebPSize = completedFiles.reduce((total, file) => total + (file.webpSize || 0), 0);

  return (
    <div className="webp-converter">
      <div className="converter-header">
        <h1>WebP Image Converter</h1>
        <p>Drag and drop your images (JPG, PNG, JPEG) to convert them to WebP format</p>
      </div>

      {/* Drop Zone */}
      <div
        className={`drop-zone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="drop-zone-content">
          <div className="drop-icon">📁</div>
          <p>Drop your images here or <span className="click-text">click to browse</span></p>
          <p className="file-types">Supports: JPG, PNG, JPEG</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>

      {/* Quality Control */}
      <div className="quality-control">
        <label htmlFor="quality-slider" className="quality-label">
          <span className="quality-text">Image Quality: {Math.round(quality * 100)}%</span>
          <span className="quality-info">
            Estimated compression: ~{Math.round((1 - quality) * 60 + 10)}% smaller
          </span>
        </label>
        <div className="quality-slider-container">
          <span className="quality-min">20%</span>
          <input
            id="quality-slider"
            type="range"
            min="0.2"
            max="1"
            step="0.05"
            value={quality}
            onChange={(e) => setQuality(parseFloat(e.target.value))}
            className="quality-slider"
          />
          <span className="quality-max">100%</span>
        </div>
        <div className="quality-presets">
          <button
            className={`preset-btn ${quality === 0.5 ? 'active' : ''}`}
            onClick={() => setQuality(0.5)}
          >
            High Compression (50%)
          </button>
          <button
            className={`preset-btn ${quality === 0.8 ? 'active' : ''}`}
            onClick={() => setQuality(0.8)}
          >
            Balanced (80%)
          </button>
          <button
            className={`preset-btn ${quality === 0.95 ? 'active' : ''}`}
            onClick={() => setQuality(0.95)}
          >
            High Quality (95%)
          </button>
        </div>
      </div>

      {/* Controls */}
      {files.length > 0 && (
        <div className="controls">
          <button
            className="btn btn-primary"
            onClick={convertAllFiles}
            disabled={converting || pendingFiles.length === 0}
          >
            {converting ? 'Converting...' : `Convert ${pendingFiles.length} Images`}
          </button>
          
          {completedFiles.length > 0 && (
            <button
              className="btn btn-success"
              onClick={downloadAsZip}
              disabled={creatingZip}
            >
              {creatingZip 
                ? 'Creating ZIP...' 
                : `Download as ZIP (${completedFiles.length} files, ${formatFileSize(totalWebPSize)})`
              }
            </button>
          )}
          
          <button
            className="btn btn-secondary"
            onClick={clearAll}
            disabled={converting || creatingZip}
          >
            Clear All
          </button>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="file-list">
          <h3>Files ({files.length})</h3>
          {files.map(file => (
            <div key={file.id} className={`file-item ${file.status}`}>
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-details">
                  <span className="file-size">Original: {formatFileSize(file.size)}</span>
                  {file.webpSize && (
                    <span className="webp-size">
                      WebP: {formatFileSize(file.webpSize)}
                      <span className="compression-ratio">
                        ({Math.round(((file.size - file.webpSize) / file.size) * 100)}% smaller)
                      </span>
                    </span>
                  )}
                </div>
              </div>

              <div className="file-status">
                {file.status === 'pending' && <span className="status-badge pending">Pending</span>}
                {file.status === 'converting' && (
                  <div className="converting">
                    <span className="status-badge converting">
                      Converting... ({Math.round(quality * 100)}% quality)
                    </span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress[file.id] || 0}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                {file.status === 'completed' && (
                  <div className="completed">
                    <span className="status-badge completed">✓ Completed</span>
                    <button
                      className="btn btn-small btn-download"
                      onClick={() => downloadFile(file)}
                    >
                      Download
                    </button>
                  </div>
                )}
                {file.status === 'error' && <span className="status-badge error">Error</span>}
              </div>

              <button
                className="remove-btn"
                onClick={() => removeFile(file.id)}
                disabled={converting}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebPConverter;